import { useTheme } from "@emotion/react";
import {
  Button,
  Stack,
  TextField,
  Typography,
  Box,
  Input,
  InputLabel,
} from "@mui/material";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { collectiveApiRequest } from "../../services";
import { totalCreditsAndRemaining } from "../../services/creditsService";
import { remainingCredits } from "../../store/reducers/auth";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

export function TechnicalDocOcr({
  apiUrl,
  setJsonData,
  setStatus,
  setloader,
  setCurlMethod,
  setCurlUrl,
  setCurlBody,
}) {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const schema = yup.object({
    applicationNumber: yup
      .string()
      .required("Application Number is required"),
  });

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  useEffect(() => {
    reset();
    setFile(null);
    setFileError("");
  }, [apiUrl]);

  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "application/pdf",
    ];

    if (!allowedTypes.includes(uploadedFile.type)) {
      setFileError("Only jpg, jpeg, png and pdf files are allowed.");
      setFile(null);
      return;
    }

    if (uploadedFile.size > 50 * 1024 * 1024) {
      setFileError("File size must be less than 50MB.");
      setFile(null);
      return;
    }

    setFileError("");
    setFile(uploadedFile);
  };

  useEffect(() => {
    if (isValid && !fileError && file) {
      setCurlMethod("POST");
      setCurlUrl(apiUrl);

      const values = getValues();

      const formData = new FormData();
      formData.append("applicationNumber", values.applicationNumber);
      formData.append("file", file);

      setCurlBody(formData);
    }
  }, [isValid, fileError, file, apiUrl]);

  const onSubmit = async (data) => {
    try {
      setloader(true);
      setJsonData(null);
      setStatus(null);

      const formData = new FormData();
      formData.append("applicationNumber", data.applicationNumber);
      formData.append("file", file);

      const res = await collectiveApiRequest(formData, apiUrl);

      setStatus(res?.status);
      setJsonData(res?.data);
      setloader(false);

      setTimeout(async () => {
        const remainingCredit = await totalCreditsAndRemaining();
        if (remainingCredit?.status === 200) {
          dispatch(
            remainingCredits({
              credits:
                remainingCredit?.data?.credit?.remainingCredit,
            })
          );
        }
      }, 2000);
    } catch (err) {
      setloader(false);
      setStatus(err?.response?.status || 500);
      setJsonData(
        err?.response?.data || { error: err.message }
      );
    }
  };

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      spacing={3}
      sx={{ mt: 2 }}
    >
      {/* Upload Box */}
      <Typography variant="body1" sx={{ color: theme.palette.grey[700] }}>
        Upload Document
      </Typography>

      <Box
        sx={{
          borderRadius: "12px",
          border: "2px dashed rgba(101, 117, 139, 0.25)",
          minHeight: "120px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        <InputLabel
          htmlFor="upload-file"
          sx={{ textAlign: "center", cursor: "pointer", width: "100%" }}
        >
          <FileUploadOutlinedIcon sx={{ width: "4rem", height: "4rem" }} />
          <Typography>{file ? file.name : "Click to upload or drag & drop"}</Typography>
        </InputLabel>

        <Input
          id="upload-file"
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </Box>

      {fileError && <Typography color="error" sx={{ fontSize: "12px" }}>{fileError}</Typography>}

      {/* Application Number Field */}
      <TextField
        label="Application Number"
        {...register("applicationNumber")}
        error={!!errors.applicationNumber}
        helperText={errors.applicationNumber?.message}
        fullWidth
        FormHelperTextProps={{ sx: { fontSize: "12px" } }}
      />

      {/* Submit */}
      <Button
        variant="contained"
        type="submit"
        disabled={!isValid || !!fileError || !file}
      >
        Send
      </Button>
    </Stack>
  );
}

TechnicalDocOcr.propTypes = {
  apiUrl: PropTypes.string,
  setJsonData: PropTypes.func,
  setStatus: PropTypes.func,
  setloader: PropTypes.func,
  setCurlMethod: PropTypes.func,
  setCurlUrl: PropTypes.func,
  setCurlBody: PropTypes.func,
};
